const API_URL = __API_URL__;
const TENANT_SLUG = 'yaatou-senegal';
const TOKEN_KEY = 'yaatou_access_token';
const REFRESH_TOKEN_KEY = 'yaatou_refresh_token';

let isRefreshing = false;
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function setTokens(accessToken: string, refreshToken: string | null) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-slug': TENANT_SLUG,
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    clearTokens();
    const err = await response.json().catch(() => ({ detail: 'Refresh failed' }));
    throw new Error(err?.detail || err?.title || 'Session expirée');
  }

  const data = await response.json();
  const accessToken = data?.data?.accessToken || data?.accessToken;
  const newRefreshToken = data?.data?.refreshToken || data?.refreshToken;

  if (!accessToken) {
    clearTokens();
    throw new Error('Invalid refresh response');
  }

  setTokens(accessToken, newRefreshToken || refreshToken);
  return accessToken;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_URL}${endpoint}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tenant-slug": TENANT_SLUG,
    ...getAuthHeader(),
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>),
    },
  });

  if (response.status === 401 && !endpoint.includes('/auth/refresh')) {
    const token = getRefreshToken();
    if (token && !isRefreshing) {
      isRefreshing = true;
      try {
        const newAccessToken = await refreshAccessToken();
        isRefreshing = false;
        refreshQueue.forEach((cb) => cb.resolve(newAccessToken));
        refreshQueue = [];

        const retryHeaders: Record<string, string> = {
          "Content-Type": "application/json",
          "x-tenant-slug": TENANT_SLUG,
          Authorization: `Bearer ${newAccessToken}`,
          ...(options.headers as Record<string, string>),
        };

        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });

        if (!retryResponse.ok) {
          const errorPayload = await retryResponse.json().catch(() => ({}));
          const message = errorPayload?.detail || errorPayload?.title || `HTTP ${retryResponse.status}`;
          const err = new Error(message) as any;
          err.status = retryResponse.status;
          err.payload = errorPayload;
          throw err;
        }

        const payload = await retryResponse.json();
        const unwrapped = payload?.data;
        if (!unwrapped) {
          throw new Error('Réponse API invalide');
        }
        return unwrapped as T;
      } catch (err) {
        isRefreshing = false;
        refreshQueue.forEach((cb) => cb.reject(err));
        refreshQueue = [];
        clearTokens();
        throw err;
      }
    } else if (token && isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: async (newAccessToken: string) => {
            try {
              const retryHeaders: Record<string, string> = {
                "Content-Type": "application/json",
                "x-tenant-slug": TENANT_SLUG,
                Authorization: `Bearer ${newAccessToken}`,
                ...(options.headers as Record<string, string>),
              };
              const retryResponse = await fetch(url, {
                ...options,
                headers: retryHeaders,
              });
              if (!retryResponse.ok) {
                const errorPayload = await retryResponse.json().catch(() => ({}));
                const message = errorPayload?.detail || errorPayload?.title || `HTTP ${retryResponse.status}`;
                const err = new Error(message) as any;
                err.status = retryResponse.status;
                err.payload = errorPayload;
                reject(err);
                return;
              }
              const payload = await retryResponse.json();
              const unwrapped = payload?.data;
              if (!unwrapped) {
                reject(new Error('Réponse API invalide'));
                return;
              }
              resolve(unwrapped as T);
            } catch (err) {
              reject(err);
            }
          },
          reject: (err: any) => reject(err),
        });
      });
    } else {
      clearTokens();
    }
  }

  if (!response.ok) {
    let errorPayload: any = null;
    try {
      errorPayload = await response.json();
    } catch {
      try {
        errorPayload = await response.text();
      } catch {
        errorPayload = null;
      }
    }

    const logId = `api_error_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    console.group(`[API Error] ${logId}`);
    console.error(`URL: ${url}`);
    console.error(`Status: ${response.status} ${response.statusText}`);
    console.error('Request options:', options);
    console.error('Response payload:', errorPayload);
    console.groupEnd();

    let message = `HTTP ${response.status}`;
    if (typeof errorPayload === 'string') {
      message = errorPayload || message;
    } else if (errorPayload && typeof errorPayload === 'object') {
      message = errorPayload?.detail || errorPayload?.title || errorPayload?.message || errorPayload?.error || message;
    }

    const err = new Error(message) as any;
    err.status = response.status;
    err.payload = errorPayload;
    err.logId = logId;
    throw err;
  }

  if (response.status === 204) {
    return null as T;
  }

  const payload = await response.json();
  const unwrapped = (payload as any)?.data;
  if (!unwrapped) {
    throw new Error('Réponse API invalide');
  }
  return unwrapped as T;
}

async function getFull<T>(
  endpoint: string,
): Promise<{ data: T; meta?: any }> {
  const url = `${API_URL}${endpoint}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tenant-slug": TENANT_SLUG,
    ...getAuthHeader(),
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({}));
    const message = errorPayload?.detail || errorPayload?.title || `HTTP ${response.status}`;
    const err = new Error(message) as any;
    err.status = response.status;
    err.payload = errorPayload;
    throw err;
  }

  if (response.status === 204) {
    return { data: null as T };
  }

  const payload = await response.json();
  return payload;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: "DELETE" }),
  getFull: <T>(endpoint: string) => getFull<T>(endpoint),
};

export { clearTokens, getRefreshToken, setTokens };
